import cn from 'classnames';
import { useFormContext, useWatch } from 'react-hook-form';
import { ErrorMessage, HelpText, Input } from '#components/atoms/forms/index.ts';
import { SelectNetwork } from '#components/patterns/entity-select/network/SelectNetwork.tsx';
import { NEW_ROLE_FLAG, NEW_ROLE_OPTION, SelectRole } from '#components/patterns/entity-select/role/index.ts';
import { DualStackAddressFields } from '#components/patterns/forms/dual-stack-address-fields/DualStackAddressFields.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { ComboboxTags } from '#components/patterns/forms/select/combobox/combobox-tags/ComboboxTags.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { Network } from '#dn-types/network.ts';
import { roleNameValidation } from '#screens/roles/validations/role-validations.ts';
import type { HostKind } from '#util/host.ts';
import { setValueAsNumberOrNull } from '#util/validation/set-value-as-number-or-null.ts';
import type { HostFormData } from '../host-form-schema.ts';
import { PublicAddressRow } from './PublicAddressRow.tsx';

type Props = {
  /** True if the form made it partway through in a failed submission, but created a role already */
  hasCreatedRole: boolean;
  /** We need to know if this is a new host or existing, because IP can't be changed */
  isEditing: boolean;
  /** Which flavor of host is being described; decides the fields and copy that differ between them */
  kind: HostKind;
  /** Networks available to the org; used to determine IP input shape based on selected network CIDRs */
  networks: readonly Network[];
  /** We only show the network select if they have more than one set up */
  shouldShowNetworkSelect: boolean;
};

export function HostGeneralFormPart({ hasCreatedRole, isEditing, kind, networks, shouldShowNetworkSelect }: Props) {
  const formContext = useFormContext<HostFormData>();
  const { register, formState, control } = formContext;
  const { errors } = formState;
  const roleID = useWatch({ control, name: 'roleID' });

  const nameExample =
    kind === 'lighthouse' ? <i>lighthouse-west-1</i>
    : kind === 'relay' ? <i>relay-west-1</i>
    : <>
        <i>laptop-alice</i> or <i>server-www-wiki</i>
      </>;

  return (
    <Spacer direction="vertical" size="wide" className="mb-24">
      <Field label="Name">
        <HelpText>
          We recommend using something you’ll recognize in logs and on your dashboard, like {nameExample}.
        </HelpText>
        <Input
          className="width-100"
          maxLength={256}
          hasError={Boolean(errors.name)}
          {...register('name')}
          autoComplete="off"
        />
        {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
      </Field>

      {kind === 'lighthouse' ?
        <PublicAddressRow />
      : null}
      {kind === 'relay' ?
        <RelayPortField />
      : null}

      <SelectRole
        className="width-100"
        control={control}
        disabled={hasCreatedRole}
        name="roleID"
        label="Role"
        help={
          kind === 'host' ?
            <HelpText>Roles determine how hosts can interact with each other through firewall rules.</HelpText>
          : kind === 'relay' ?
            <HelpText>
              The firewall rules with this role are only used when interacting with the relay directly, not when it is
              functioning as a relay.
            </HelpText>
          : <HelpText>
              The firewall rules with this role are only used when interacting with the lighthouse directly, not when it
              is functioning as a lighthouse.
            </HelpText>

        }
        errorMessage={errors.roleID?.message}
        additionalItems={[NEW_ROLE_OPTION]}
      />
      {roleID === NEW_ROLE_FLAG && (
        <Field label="Role name">
          <Input
            className="width-100"
            maxLength={50}
            hasError={Boolean(errors.roleName)}
            {...register('roleName', { ...roleNameValidation, disabled: hasCreatedRole })}
          />
          {errors.roleName && <ErrorMessage>{errors.roleName.message}</ErrorMessage>}
        </Field>
      )}
      <SelectNetwork
        label="Network"
        name="networkID"
        control={control}
        // Cannot change a network after host is created
        disabled={isEditing}
        className={cn('width-100', { 'visually-hidden': !shouldShowNetworkSelect })}
        errorMessage={errors.networkID?.message}
      />
      <DualStackAddressFields networks={networks} disabled={isEditing} />
      <ComboboxTags
        name="tags"
        control={control}
        hasError={Boolean(errors.tags)}
        helpText="Tags are facets of identity and take the form “key:value” (e.g. “env:prod”)."
      />
      {errors.tags && <ErrorMessage>{errors.tags.message}</ErrorMessage>}
    </Spacer>
  );
}

/** Relays advertise a bare port rather than the `address:port` pair a lighthouse needs. */
function RelayPortField() {
  const {
    register,
    formState: { errors },
  } = useFormContext<HostFormData>();

  return (
    <Field label="Port">
      <HelpText>Be sure to open the firewall for this UDP port on the machine.</HelpText>
      <Input
        className="width-100"
        type="number"
        // One beyond the real 0–65535 range: the form has no noValidate, so honest
        // bounds let native validation block the submit before zod styles the error.
        min={0}
        max={65536}
        hasError={Boolean(errors.listenPort)}
        inputMode="numeric"
        {...register('listenPort', {
          setValueAs: setValueAsNumberOrNull,
        })}
      />
      {errors.listenPort && <ErrorMessage>{errors.listenPort.message}</ErrorMessage>}
    </Field>
  );
}
